import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingDurbanvillePaintersMainPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Durbanville Painters - Expert House Painting Services | Interior & Exterior Specialists',
    description: 'Professional house painters serving Durbanville, Tygervalley, and Northern Suburbs. Expert interior and exterior painting with over 16 years of experience. Quality workmanship, competitive rates, and customer satisfaction guaranteed.',
    keywords: 'Durbanville painters, house painters Durbanville, interior painting, exterior painting, residential painters, Tygervalley painters, Northern Suburbs painting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Durbanville Painters',
          description: 'Expert house painting services in Durbanville and surrounding areas',
          address: 'Durbanville, Cape Town',
          priceRange: '$$',
          serviceArea: ['Durbanville', 'Tygervalley', 'Bellville', 'Brackenfell', 'Plattekloof', 'Welgemoed']
        }),
        generateServiceSchema({
          name: 'House Painting Services',
          description: 'Interior and exterior painting for residential properties',
          provider: 'Durbanville Painters',
          areaServed: ['Durbanville', 'Tygervalley', 'Northern Suburbs'],
          serviceType: ['Interior Painting', 'Exterior Painting', 'House Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Durbanville Painters",
    location: "Durbanville, Cape Town",
    website: "https://durbanvillepainters.co.za/",
    heroImage: "https://public.readdy.ai/ai/img_res/7f5a8dbc5c502a3ca58cc31422fc1d2c.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20modern%20durbanville%20home%20exterior%20with%20cream%20walls%20contemporary%20design%20professional%20finish%20manicured%20suburban%20garden%20clear%20blue%20sky&width=800&height=600&seq=gallery-dp1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=interior%20living%20room%20painting%20durbanville%20home%20elegant%20neutral%20colors%20modern%20furniture%20professional%20painter%20working%20clean%20finish%20natural%20lighting&width=800&height=600&seq=gallery-dp2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=house%20exterior%20painting%20project%20durbanville%20residential%20area%20professional%20painters%20scaffolding%20suburban%20setting%20contemporary%20architecture%20quality%20workmanship&width=800&height=600&seq=gallery-dp3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=freshly%20painted%20bedroom%20interior%20durbanville%20home%20sophisticated%20color%20scheme%20modern%20decor%20professional%20finish%20comfortable%20living%20space&width=800&height=600&seq=gallery-dp4&orientation=landscape"
    ],
    services: [
      {
        title: "Interior House Painting",
        description: "Transform your Durbanville home's interior with our expert painting services. We specialize in creating beautiful, comfortable living spaces that reflect your personal style and enhance your property's value. Our team understands the latest color trends and application techniques to deliver stunning results that stand the test of time.",
        features: ["Living rooms and family areas", "Bedrooms and nurseries", "Kitchens and dining rooms", "Bathrooms and powder rooms", "Home offices and studies", "Basements and entertainment areas", "Ceilings and crown molding", "Feature walls and accent painting", "Color consultation services", "Wallpaper removal and installation", "Texture painting techniques", "Eco-friendly paint options"]
      },
      {
        title: "Exterior House Painting",
        description: "Protect and beautify your Durbanville property with our professional exterior painting services. We use premium weather-resistant paints specifically designed for the Northern Suburbs climate, ensuring your home's exterior looks great and stays protected from the elements for years to come.",
        features: ["Complete house exterior painting", "Boundary walls and perimeter fences", "Garage doors and entry gates", "Window frames and shutters", "Fascia boards and eaves", "Balconies and outdoor structures", "Roof painting and treatment", "Pressure washing and preparation", "Wood staining and sealing", "Metal surface treatments", "Damp proofing applications", "Color matching services"]
      },
      {
        title: "Specialized Painting Services",
        description: "Beyond standard house painting, we offer specialized services to address specific needs and enhance your property's functionality and appearance. Our expertise includes protective coatings, decorative finishes, and maintenance solutions tailored to Durbanville's unique environmental conditions.",
        features: ["Roof painting and waterproofing", "Damp proofing and sealing", "Wood deck staining", "Concrete floor coatings", "Cabinet painting and refinishing", "Decorative faux finishes", "Textured wall treatments", "Graffiti removal services", "Touch-up and maintenance", "Holiday home preparation", "Pre-sale painting services", "Insurance repair painting"]
      }
    ],
    serviceAreas: ["Durbanville", "Tygervalley", "Bellville", "Brackenfell", "Plattekloof", "Welgemoed", "Sonstraal Heights", "Durbanville Hills", "De Bron", "Vierlanden", "Aurora", "Eversdal"],
    about: "Durbanville Painters is a trusted local painting company dedicated to serving homeowners throughout Durbanville and the surrounding Northern Suburbs of Cape Town. With over 16 years of experience in residential painting, we have built a solid reputation for delivering exceptional quality, personalized service, and competitive value. Our team of skilled painters takes pride in transforming homes with precision, care, and attention to detail that exceeds expectations.",
    aboutExtended: [
      "Founded in 2007 by local Durbanville residents, our company understands the unique character and requirements of homes in this beautiful area. We have painted hundreds of properties across Durbanville, from charming older homes in established neighborhoods to modern houses in new developments like Durbanville Hills and Tygervalley. Our intimate knowledge of local architectural styles, weather patterns, and homeowner preferences enables us to provide tailored solutions that perfectly match each property's needs.",
      "Our commitment to quality begins with our team selection and training. Every painter on our crew is experienced, reliable, and dedicated to maintaining the highest standards of workmanship. We invest in ongoing training to ensure our team stays current with the latest painting techniques, safety procedures, and product innovations. Our painters are not just skilled craftspeople – they are courteous professionals who respect your home and family throughout the painting process.",
      "We believe in using only the finest quality paints and materials available. Our partnerships with leading brands like Dulux, Plascon, and Prominent Paints ensure we have access to the most advanced products designed specifically for South African conditions. We carefully select paints based on factors such as durability, color retention, environmental impact, and suitability for Durbanville's climate, ensuring your investment delivers lasting beauty and protection.",
      "Customer satisfaction is at the heart of everything we do. We take the time to understand your vision, provide honest advice about colors and finishes, and work within your budget and timeline. Our detailed preparation process, careful application techniques, and thorough cleanup ensure a smooth, hassle-free experience from start to finish. We stand behind our work with comprehensive warranties and are always available for follow-up service when needed.",
      "As a local Durbanville business, we are committed to contributing positively to our community. We support local suppliers when possible, employ local residents, and take pride in enhancing the beauty and value of properties throughout our neighborhood. When you choose Durbanville Painters, you're not just getting exceptional painting services – you're supporting a business that cares about our shared community."
    ],
    ourApproach: [
      "Personalized consultation and color guidance",
      "Detailed written estimates with no hidden costs",
      "Thorough surface preparation and repair",
      "Premium quality paints and materials",
      "Skilled application and finishing techniques",
      "Clean, organized, and respectful work practices",
      "Daily progress communication",
      "Meticulous cleanup and site restoration",
      "Final walkthrough and quality inspection",
      "Comprehensive warranty coverage",
      "Ongoing customer support and service"
    ],
    whyChoose: [
      "16+ years serving Durbanville residents",
      "Hundreds of satisfied local customers",
      "Licensed and fully insured",
      "Experienced local painting team",
      "Premium quality paints and materials",
      "Competitive and transparent pricing",
      "Free detailed estimates",
      "Comprehensive workmanship warranty",
      "Excellent local reputation and reviews",
      "Specialized Northern Suburbs expertise",
      "Personalized customer service",
      "Environmentally responsible practices",
      "Flexible scheduling options",
      "Post-project support available"
    ],
    testimonials: [
      {
        name: "Susan van der Walt",
        rating: 5,
        text: "Durbanville Painters did a fantastic job on our home in Tygervalley! The team was professional, punctual, and the quality of work is outstanding. They provided excellent color advice and the finish is absolutely beautiful. Highly recommend to all Durbanville residents!"
      },
      {
        name: "Peter Jacobs",
        rating: 5,
        text: "We've used Durbanville Painters twice now and they never disappoint. Great attention to detail, fair pricing, and they always clean up properly. Our house looks amazing and several neighbors have asked for their contact details. Excellent local service!"
      },
      {
        name: "Maria Botha",
        rating: 5,
        text: "Very impressed with their professionalism and quality! They painted our entire home exterior and interior and managed everything perfectly. The team was respectful, efficient, and delivered exactly what they promised. Will definitely use them again!"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Durbanville Painters</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Durbanville Painters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve homeowners throughout Durbanville and surrounding areas:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our House Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">Complete painting solutions for your Durbanville home, inside and out.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Durbanville & surrounding areas</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: 8:30AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by leaderr
              </a>
              <span>•</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}